import React from 'react';

import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { ArrowForward } from '@tmap/mmm-style-guide/src/Icon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import useFormattedDate from '@tmap/mmm-style-guide/src/ssr/useFormattedDate';

import imageUrlBuilder from '@sanity/image-url';
import Link from 'next/link';
import client from '../client';
import ConditionalLazyLoad from './conditional-lazy-load';
import PreloadablePicture from './preloadablePicture';

const builder = imageUrlBuilder(client);

const heroStyle = () => ({
  width: '100%',
  height: 'auto',
});
const HeroPreloader = styled(PreloadablePicture)(heroStyle);
const HeroImg = styled('img')(heroStyle);

const ContentWrapper = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const contentStyle = () => ({
  maxWidth: '100%',
});
const ContentPicture = styled('picture')(contentStyle);
const ContentImg = styled('img')(contentStyle);

function NewsCard(props) {
  const {
    article,
    shouldLazyLoad = true,
    lazyProps = {},
  } = props;

  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/news/${article.slug.current}`;
  const createdAt = useFormattedDate(article._createdAt, 'MMM d, yyyy');

  return (
    <Card>
      {article.heroImage && (
        <ConditionalLazyLoad shouldLazyLoad={shouldLazyLoad} {...lazyProps}>
          <Link href={canonicalUrl}>
            <HeroPreloader preload={!shouldLazyLoad}>
              <source
                srcSet={
                  builder.image(article.heroImage)
                    .format('webp')
                    .width(391)
                    .height(217)
                    .url()
                }
              />
              <HeroImg
                src={
                  builder.image(article.heroImage)
                    .width(391)
                    .height(217)
                    .url()
                }
                alt={(article.heroImage || {}).alt}
                width={391}
                height={217}
              />
            </HeroPreloader>
          </Link>
        </ConditionalLazyLoad>
      )}
      <ContentWrapper container spacing={6} alignItems='center'>
        {!article.heroImage && article.pressLogo && (
          <Grid item xs={12}>
            <ContentPicture>
              <source
                srcSet={
                  builder.image(article.pressLogo)
                    .format('webp')
                    .maxWidth(300)
                    .maxHeight(80)
                    .url()
                }
              />
              <ContentImg
                src={
                  builder.image(article.pressLogo)
                    .maxWidth(300)
                    .maxHeight(80)
                    .url()
                }
                alt={(article.pressLogo || {}).alt}
              />
            </ContentPicture>
          </Grid>
        )}
        {!article.pressLogo && article.pressName && (
          <Grid item xs={12}>
            <Typography variant='body2'>{article.pressName}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Link href={canonicalUrl}>
            <Typography variant='h6' color='dark'>{article.title}</Typography>
          </Link>
        </Grid>
        <Grid item xs>
          <Typography variant='body2' color='textSecondary'>{createdAt}</Typography>
        </Grid>
        <Grid item>
          <Link href={canonicalUrl}>
            <Button variant='text' color='secondary'>Read Article&nbsp;&nbsp;<ArrowForward /></Button>
          </Link>
        </Grid>
      </ContentWrapper>
    </Card>
  );
}

export default NewsCard;
